import { useToast } from '@chakra-ui/react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxios } from 'context/AxiosContextProvider';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { useEffect } from 'react';

import { PaginatedAnnouncement } from '../api/types';
import config from '../config';

interface AnnouncementParams {
  searchString: string | undefined;
  page: number | undefined;
  itemsPerPage: number | undefined;
  country: string | undefined;
  startDate?: string | null | undefined;
  endDate?: string | null | undefined;
}

interface GetAnnouncements extends AnnouncementParams {
  axios: AxiosInstance;
}
async function getAnnouncement({
  axios,
  searchString,
  page,
  itemsPerPage,
  country,
  startDate,
  endDate,
}: GetAnnouncements): Promise<PaginatedAnnouncement> {
  const { data } = await axios.get(`${config.apiBaseUrl}/op/v1/announcement`, {
    params: {
      searchString: searchString || '',
      page,
      itemsPerPage,
      country,
      start: startDate,
      end: endDate,
    },
  });
  return data;
}

export default function useAnnouncementsQuery({ searchString, page, itemsPerPage, country, startDate, endDate }: AnnouncementParams) {
  const axios = useAxios();
  const toast = useToast();

  const query = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: getKey(searchString, page, itemsPerPage, country, startDate, endDate),
    queryFn: () =>
      getAnnouncement({
        axios,
        searchString,
        page,
        itemsPerPage,
        country,
        startDate,
        endDate,
      }),
    notifyOnChangeProps: ['data', 'isFetching', 'error'],
    retry: false,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (query.isError) {
      manageErrorResponse(toast, query.error as any);
    }
  }, [query.error, query.isError, toast]);

  return query;
}

function getKey(
  searchString: string | undefined,
  page: number | undefined,
  itemsPerPage: number | undefined,
  country: string | undefined,
  startDate?: string | undefined | null,
  endDate?: string | undefined | null,
) {
  return ['Announcements', searchString, page, itemsPerPage, country, startDate, endDate] as const satisfies QueryKey;
}

useAnnouncementsQuery.getKey = getKey;
